import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { NVLineChartDownsampled } from "common/graphs/DownSampling";
import {
  memoryUsageAndStatsRetrieval,
  parseStatsMemData,
} from "views/Statistics/System/SysStatsMem/api";
import Loading from "common/Loading";
import * as d3 from "d3";
import BqnContext from "common/BqnContext";
import { calcRangeExtent } from "common/api";
import {
  PERIOD_DASHBOARD_TO_HOURS,
  PERIOD_DASHBOARD_PERIOD_MAP,
  LOAD_INTERVAL,
} from "./utils";
import useDashboardData from "./useDashboardData";
import { PERIOD_TO_LINES } from "common/constants";

const Wrapper = styled.div`
  width: 100%;
  .legends {
    ul {
      display: flex;
      li {
        display: inline-block;
        margin: 0;

        .legend {
          width: auto;
        }
      }
    }
  }
  svg {
    width: 100%;
    height: 200px;

    .nv-axisMaxMin.nv-axisMaxMin-y.nv-axisMax-y {
      display: none;
    }
  }
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--text-primary);
`;

const formatTimeTooltip = d3.timeFormat("%Y-%m-%d  %H:%M:%S");
const formatPercent = d3.format(".1f");
const formatGB = d3.format(".3f");
const xAxisTimeFormat = d3.timeFormat("%m/%d %H:%M");
const FIXED_Y_RANGE = [0, 1];

function tooltipContentGenerator(totals) {
  return (d) => {
    const date = formatTimeTooltip(new Date(d.value));
    const ROW_MAX = 16;
    const rowCnt = Math.min(d.series.length, ROW_MAX);
    const rows = Array(rowCnt).fill("");

    d.series.forEach((series, i) => {
      const value = series.value;
      if (isNaN(value) || value === null) return;
      const rowIdx = i % ROW_MAX;
      const gbValue = (value * totals[i]) / 102400;
      rows[rowIdx] +=
        `<td class="legend-color-guide"><div style="background-color:${series.color};"></div></td><td>${series.key}</td><td align="right"><b>${formatPercent(value)} %</b></td><td align="right"><b>${formatGB(gbValue)} GB</b></td>`;
    });

    const htmlRows = rows.map((row) => `<tr>${row}</tr>`).join("");
    return `<b style="white-space: pre;"> ${date}  </b><br/><table>${htmlRows}</table>`;
  };
}

// The totals are indexed by BQN, and the response of a command with the
// "bqn-id ID" option reports no BQN ID, so the BQN selected is the one its
// data is attributed to. The BQN serving the GUI is the one shown while the
// whole cluster is selected.
const getMemoryUsageData = ({ mode, bqnId, loginBqnId, period }) => {
  const periodMem = PERIOD_DASHBOARD_PERIOD_MAP[period];
  const bqnIdShown = bqnId === "all" ? loginBqnId : bqnId;

  return memoryUsageAndStatsRetrieval(
    bqnIdShown,
    mode,
    bqnId,
    PERIOD_TO_LINES[periodMem],
  ).then(([System, Mpool, Dpdk, SysDate, statsResponse]) => {
    const totals = {
      System: System[bqnIdShown],
      Mpool: Mpool[bqnIdShown],
      Dpdk: Dpdk[bqnIdShown],
    };

    return {
      totalsRange: {
        totals,
        range: calcRangeExtent(SysDate, PERIOD_DASHBOARD_TO_HOURS[period]),
      },
      data: parseStatsMemData(statsResponse, periodMem, totals),
    };
  });
};

function MemoryUsage({ period }) {
  // The BQN of the session is the one the totals are shown of while the
  // whole cluster is selected, and it is not one of the retrieval.
  const { loginBqnId } = useContext(BqnContext);

  // The chart has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data: dataRetrieved, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId }) =>
      getMemoryUsageData({ mode, bqnId, loginBqnId, period }),
    onError: "Error fetching memory usage data:",
    deps: [period, loginBqnId],
    interval: LOAD_INTERVAL,
  });

  const { totalsRange, data } = dataRetrieved || {};

  const chartFields = useMemo(() => {
    return data
      ? data.fields.map((field) => ({ name: field, label: field }))
      : [];
  }, [data]);

  const tooltipGen = useMemo(() => {
    if (!totalsRange) return () => "";
    return tooltipContentGenerator([
      totalsRange.totals.System,
      totalsRange.totals.Mpool,
      ...(totalsRange.totals.Dpdk || []),
    ]);
  }, [totalsRange]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!totalsRange || !data) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  const totalSystem =
    totalsRange.totals?.System === undefined
      ? null
      : `${(totalsRange.totals.System / 1024).toFixed(2)} GB`;

  return (
    <Wrapper>
      <Subtitle>
        System Total: <strong>{totalSystem}</strong>
      </Subtitle>
      <NVLineChartDownsampled
        xField="time"
        items={data.items}
        range={totalsRange.range}
        yAxisUnits={"%"}
        xAxisFormat={xAxisTimeFormat}
        fields={chartFields}
        yAxisTopGap={0.05}
        yAxisTicksNumber={5}
        xAxisTicksNumber={5}
        tooltipContentGenerator={tooltipGen}
        fixedYRange={FIXED_Y_RANGE}
      />
    </Wrapper>
  );
}

export default React.memo(MemoryUsage);
